import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../../api/client";
import MetricCard from "../../components/MetricCard";
import "./InventoryOverviewTab.css";

function StockStatusBadge({ status }) {
  const map = {
    out: { bg: "#fee2e2", color: "#dc2626", label: "Out of Stock" },
    low: { bg: "#fff7ed", color: "#c2410c", label: "Low Stock" },
    ok: { bg: "#dcfce7", color: "#15803d", label: "OK" },
  };
  const s = map[status];
  return (
    <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}33`, borderRadius: 999, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
      {s.label}
    </span>
  );
}

export default function InventoryOverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/manufacturing/inventory/dashboard");
      setData(res.data.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  const metrics = data?.metrics;
  const lowStockParts = data?.lowStockParts || [];
  const recentMovements = data?.recentMovements || [];

  return (
    <div>
      <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        <MetricCard label="Total Parts" value={loading ? "—" : metrics?.totalParts} />
        <MetricCard label="Low Stock" value={loading ? "—" : metrics?.lowStockCount} accent={metrics?.lowStockCount > 0 ? "#d97706" : undefined} />
        <MetricCard label="Out of Stock" value={loading ? "—" : metrics?.outOfStockCount} accent={metrics?.outOfStockCount > 0 ? "#dc2626" : undefined} />
        <MetricCard label="Movements Today" value={loading ? "—" : metrics?.movementsToday} accent="#16a34a" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 16 }} className="inv-overview-grid">
        <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
            {lowStockParts.length > 0 && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#dc2626", display: "inline-block" }} />}
            <h2 style={{ margin: 0, fontSize: 15, color: "#0f2d5c" }}>Stock Alerts</h2>
          </div>
          {loading ? (
            <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
          ) : lowStockParts.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "#15803d", fontWeight: 600 }}>
              No stock alerts. All parts are sufficiently stocked.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {["Part Name", "Part Code", "Current Qty", "Min Level", "Status"].map((h) => (
                      <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lowStockParts.map((p) => (
                    <tr key={p.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "10px 16px", fontWeight: 600, color: "#0f2d5c" }}>{p.name}</td>
                      <td style={{ padding: "10px 16px", color: "#64748b", fontFamily: "monospace" }}>{p.code}</td>
                      <td style={{ padding: "10px 16px" }}>{p.currentStock} {p.unitOfMeasure}</td>
                      <td style={{ padding: "10px 16px" }}>{p.minStockLevel != null ? `${p.minStockLevel} ${p.unitOfMeasure}` : "—"}</td>
                      <td style={{ padding: "10px 16px" }}>
                        <StockStatusBadge status={p.currentStock === 0 ? "out" : "low"} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ margin: 0, fontSize: 15, color: "#0f2d5c" }}>Recent Movements</h2>
            <Link to="/manufacturing/inventory/movements" style={{ fontSize: 12, color: "#1d4ed8", fontWeight: 700, textDecoration: "none" }}>View All →</Link>
          </div>
          {loading ? (
            <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
          ) : recentMovements.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>No movements yet.</div>
          ) : (
            <div style={{ padding: "6px 0" }}>
              {recentMovements.map((m) => (
                <div key={m.id} style={{ padding: "10px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: "#0f2d5c", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.partMaster?.name}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      {new Date(m.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      {" · by "}{m.loggedBy ? `${m.loggedBy.firstName} ${m.loggedBy.lastName}` : "—"}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <span style={{
                      background: m.type === "in" ? "#dcfce7" : "#fee2e2", color: m.type === "in" ? "#15803d" : "#dc2626",
                      borderRadius: 999, padding: "2px 8px", fontSize: 11, fontWeight: 800, marginRight: 6,
                    }}>
                      {m.type === "in" ? "IN" : "OUT"}
                    </span>
                    <span style={{ fontWeight: 700, color: m.type === "in" ? "#15803d" : "#dc2626" }}>
                      {m.type === "in" ? "+" : "-"}{m.quantity}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
